/**
 * 營運中心詳情頁 — 素材庫管理 + 展示間入口
 * 動態路由 /ee_ops_centers/:code
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Button, Chip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableFooter, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tooltip, Divider, Collapse, Autocomplete, Checkbox,
  Tabs, Tab, Stack, FormControlLabel, Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Slideshow as SlideshowIcon,
  Refresh as RefreshIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  AutoAwesome as AutoAwesomeIcon,
  KeyboardArrowDown as ChevronDownIcon,
  KeyboardArrowRight as ChevronRightIcon,
  ManageSearch as ResearchIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  LocalOffer as CategoryIcon,
  Sell as TagIcon,
  Add as AddIcon,
  Assessment as ReportIcon,
  PlayCircleOutline as PlayCircleIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../components/FunctionPageHeader';
import { useMessage } from '../contexts/MessageContext';
import {
  getCenterByCode, listMaterials, deleteMaterial, updateMaterial,
  importResearchItems, getMaterialImageUrl,
  uploadFile, analyzeMaterial, bulkAnalyze,
  listTags, createTag, updateTag, deleteTag,
  setMaterialTags, bulkAssignTags, aggregateTags, listMaterialsByTag,
  bulkDeleteMaterials,
} from '../services/opsCenterService';
import {
  OpsCenter, OpsMaterial, OpsTag, OpsTagCreate, TagAggregateEntry,
} from '../types/opsCenter';
import { listTopics } from '../services/researchService';
import { Topic } from '../types/research';
import { getI18nValue } from '../utils/i18nHelper';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import { usePermission } from '../hooks/usePermission';
import AuthImage from '../components/AuthImage';

const FUNC_CODE = 'ee_ops_centers';

const OpsCenterDetailPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError, showApiError } = useMessage();
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');

  const [center, setCenter] = useState<OpsCenter | null>(null);
  const [materials, setMaterials] = useState<OpsMaterial[]>([]);
  const [loading, setLoading] = useState(true);

  const [importOpen, setImportOpen] = useState(false);
  const [importTopicCode, setImportTopicCode] = useState('');

  const [editMaterial, setEditMaterial] = useState<OpsMaterial | null>(null);

  const docFileRef = React.useRef<HTMLInputElement>(null);    // PPT / PDF 單檔
  const imgFileRef = React.useRef<HTMLInputElement>(null);    // 照片 / 圖片 多檔
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState<number | 'bulk' | null>(null);

  // 上傳對話框狀態（支援多檔；共用同一個 category）
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadCategory, setUploadCategory] = useState<string>('');
  // 多檔上傳進度：{ current: 第幾個（1-based）, total, currentName }
  const [uploadProgress, setUploadProgress] = useState<{
    current: number; total: number; currentName: string;
  } | null>(null);

  // 編輯對話框內的 category（與 editMaterial 同步）
  const [editCategory, setEditCategory] = useState<string>('');

  // 勾選重新分類（僅對 image / research_item / note 可用，PPT/PDF 不可動）
  const RECLASSIFIABLE_TYPES = ['image', 'research_item', 'note'] as const;
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState<string>('');
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Tabs：library / tags / report
  type TabKey = 'library' | 'tags' | 'report';
  const [currentTab, setCurrentTab] = useState<TabKey>('library');

  // Tag 字典 + 編輯狀態
  const [tags, setTags] = useState<OpsTag[]>([]);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<OpsTag | null>(null);  // null=新增模式
  const [tagForm, setTagForm] = useState<OpsTagCreate>({
    tag_code: '', tag_name: '', description: '', color: '#1976d2',
    sort_order: 0, is_active: true,
  });

  // 批次掛 tag 對話框
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const [bulkTagIds, setBulkTagIds] = useState<number[]>([]);

  // 編輯 material 時的 tag 選取（與 editMaterial 同步）
  const [editTagIds, setEditTagIds] = useState<number[]>([]);

  // Tag 報告
  const [tagAggregate, setTagAggregate] = useState<TagAggregateEntry[]>([]);
  const [aggregating, setAggregating] = useState(false);
  const [expandedTagId, setExpandedTagId] = useState<number | null>(null);
  const [tagMaterials, setTagMaterials] = useState<OpsMaterial[]>([]);

  // 議題下拉選項（給匯入對話框用）
  const [topics, setTopics] = useState<Topic[]>([]);

  const loadTags = useCallback(async () => {
    if (!center) return;
    try {
      const ts = await listTags(center.id);
      setTags(ts);
    } catch (e) { showApiError(e); }
  }, [center, showApiError]);

  const loadAggregate = useCallback(async () => {
    if (!center) return;
    setAggregating(true);
    try {
      const r = await aggregateTags(center.id);
      setTagAggregate(r);
    } catch (e) { showApiError(e); }
    finally { setAggregating(false); }
  }, [center, showApiError]);

  // 收折狀態（key = group key），首次載入後預設全部收合
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // 用 ref 追蹤是否已經套過預設收合（避免 useEffect 在使用者操作後又覆寫）
  const collapseInitRef = React.useRef(false);
  // 分頁狀態（每個 group 獨立）
  const [groupPage, setGroupPage] = useState<Record<string, number>>({});
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const load = useCallback(async () => {
    if (!code) return;
    setLoading(true);
    try {
      const c = await getCenterByCode(code);
      setCenter(c);
      const mats = await listMaterials(c.id);
      setMaterials(mats);
      logView(FUNC_CODE, { code, center_id: c.id, material_count: mats.length });
    } catch (e: any) {
      logView(FUNC_CODE, { code }, String(e?.message || e));
      showApiError(e);
    }
    finally { setLoading(false); }
  }, [code, showApiError]);

  useEffect(() => { load(); }, [load]);

  // 載入 tag 字典（中心載好後一併拉）
  useEffect(() => { loadTags(); }, [loadTags]);

  // 載入議題清單（給匯入對話框下拉用；研究模組未啟用時靜默失敗）
  useEffect(() => {
    (async () => {
      try {
        const ts = await listTopics({ is_active: true });
        setTopics(ts);
      } catch { /* 容錯 */ } // silent ok: optional topic list for filter, non-blocking
    })();
  }, []);

  // 切到 Tag 報告 tab 時刷新 aggregate
  useEffect(() => {
    if (currentTab === 'report' && center) loadAggregate();
  }, [currentTab, center, loadAggregate]);

  // 重置收合預設旗標（換中心時重新生效）
  useEffect(() => {
    collapseInitRef.current = false;
  }, [code]);

  // 開啟編輯對話框時，從 source_metadata 同步 category + 從 material.tag_ids 同步 tags
  useEffect(() => {
    if (editMaterial) {
      const meta = (editMaterial.source_metadata ?? {}) as Record<string, unknown>;
      const cat = typeof meta.category === 'string' ? meta.category : '';
      setEditCategory(cat);
      setEditTagIds(editMaterial.tag_ids ?? []);
    } else {
      setEditCategory('');
      setEditTagIds([]);
    }
  }, [editMaterial]);

  const handleImport = async () => {
    if (!center || !importTopicCode.trim()) return;
    const intent = { action: 'import_research', center_id: center.id,
                     topic_code: importTopicCode.trim() };
    try {
      const r = await importResearchItems(center.id, {
        topic_code: importTopicCode.trim(),
        starting_order: materials.length,
      });
      if (r.ok) {
        logCreate(FUNC_CODE, { ...intent, result: r });
        showSuccess('SYS020001', { name: r.message });
      } else {
        logCreate(FUNC_CODE, intent, r.message);
        showError('ERR020004', { detail: r.message });
      }
      setImportOpen(false);
      setImportTopicCode('');
      await load();
    } catch (e: any) {
      logCreate(FUNC_CODE, intent, String(e?.message || e));
      showApiError(e);
    }
  };

  const handleDelete = async (m: OpsMaterial) => {
    if (!window.confirm(t('opsCenter.detail.messages.deleteMaterialConfirm', { name: m.title }))) return;
    try {
      await deleteMaterial(m.id);
      logDelete(FUNC_CODE, { material_id: m.id, title: m.title, type: m.material_type });
      showSuccess('SYS020003', { name: m.title });
      await load();
    } catch (e: any) {
      logDelete(FUNC_CODE, { material_id: m.id, title: m.title }, String(e?.message || e));
      showApiError(e);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      // 後端上限 100/次；超量前端分批送。
      for (let i = 0; i < ids.length; i += 100) {
        await bulkDeleteMaterials(ids.slice(i, i + 100));
      }
      logDelete(FUNC_CODE, { action: 'bulk_delete', material_ids: ids, count: ids.length });
      showSuccess('SYS020003', { name: t('opsCenter.detail.library.bulkDeleteName', { count: ids.length }) });
      setBulkDeleteOpen(false);
      setSelectedIds(new Set());
      await load();
    } catch (e: any) {
      logDelete(FUNC_CODE, { action: 'bulk_delete', material_ids: ids }, String(e?.message || e));
      showApiError(e);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!center || pendingFiles.length === 0) return;
    const userCat = uploadCategory.trim();
    const files = pendingFiles;
    setUploadDialogOpen(false);
    setUploading(true);

    // 整批上傳共用同一個時間戳（YYYY-MM-DD HH:MM）；
    // 用於非 PPT/PDF 的預設分類，這樣同一次上傳的圖片/雜項會聚在同一組
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const batchTimestamp =
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} `
      + `${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const isPptxOrPdf = (name: string) => /\.(pptx|pdf)$/i.test(name);

    let okCount = 0;
    let totalMaterials = 0;
    const failed: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadProgress({ current: i + 1, total: files.length, currentName: f.name });

        // category 決策：
        // 1. 使用者輸入了字串 → 整批用該分類（包含 PPT/PDF）
        // 2. 否則 PPT/PDF 不傳，讓後端自動用檔名
        // 3. 否則（圖片等）用 batchTimestamp，整批同一個時間戳
        const cat = userCat
          ? userCat
          : (isPptxOrPdf(f.name) ? undefined : batchTimestamp);

        try {
          const r = await uploadFile(center.id, f, false, cat);
          if (r.ok) {
            okCount += 1;
            totalMaterials += r.created || 0;
          } else {
            failed.push(`${f.name}：${r.message}`);
          }
        } catch (e: unknown) {
          const msg = (e as { message?: string })?.message ?? String(e);
          failed.push(`${f.name}：${msg}`);
        }
      }
    } finally {
      setUploadProgress(null);
      setUploading(false);
      setPendingFiles([]);
      setUploadCategory('');
      await load();
    }

    const summary = {
      action: 'upload_batch',
      center_id: center.id,
      file_count: files.length,
      ok_count: okCount,
      material_count: totalMaterials,
      category: userCat || `auto:${batchTimestamp}`,
      failed_count: failed.length,
      failed_names: failed.slice(0, 5),  // 前 5 筆，避免日誌肥大
    };

    if (failed.length === 0) {
      logCreate(FUNC_CODE, summary);
      const suffix = userCat
        ? t('opsCenter.detail.messages.uploadCatSuffixFromUser', { cat: userCat })
        : t('opsCenter.detail.messages.uploadCatSuffixDefault', { ts: batchTimestamp });
      showSuccess('SYS020001', {
        name: t('opsCenter.detail.messages.uploadSuccess',
          { count: okCount, total: totalMaterials, suffix }),
      });
    } else if (okCount > 0) {
      logCreate(FUNC_CODE, summary, `partial: ${failed.length} failed`);
      showError('ERR020004', {
        detail: t('opsCenter.detail.messages.uploadPartial',
          { ok: okCount, total: files.length, detail: failed.join('；') }),
      });
    } else {
      logCreate(FUNC_CODE, summary, `all failed: ${failed.join('；')}`);
      showError('ERR020004', {
        detail: t('opsCenter.detail.messages.uploadFailed', { detail: failed.join('；') }),
      });
    }
  };

  const handleAnalyzeOne = async (m: OpsMaterial) => {
    setAnalyzing(m.id);
    const intent = { action: 'analyze_material', material_id: m.id, title: m.title };
    try {
      const r = await analyzeMaterial(m.id);
      logUpdate(FUNC_CODE,
        { ...intent, before_summary: m.ai_summary },
        { ...intent, after_summary: r.ai_summary });
      showSuccess('SYS020002', { name: `素材 #${m.id}` });
      await load();
    } catch (e: any) {
      logUpdate(FUNC_CODE, intent, intent, String(e?.message || e));
      showApiError(e);
    }
    finally { setAnalyzing(null); }
  };

  const handleBulkAnalyze = async () => {
    if (!center) return;
    if (!window.confirm(t('opsCenter.detail.messages.bulkAnalyzeConfirm'))) return;
    setAnalyzing('bulk');
    const intent = { action: 'bulk_analyze', center_id: center.id };
    try {
      const r = await bulkAnalyze(center.id);
      logUpdate(FUNC_CODE, intent, { ...intent, result: r });
      showSuccess('SYS020002', { name: r.message });
      await load();
    } catch (e: any) {
      logUpdate(FUNC_CODE, intent, intent, String(e?.message || e));
      showApiError(e);
    }
    finally { setAnalyzing(null); }
  };

  const handleSaveEdit = async () => {
    if (!editMaterial) return;
    const original = { id: editMaterial.id, title: editMaterial.title,
                       raw_text: editMaterial.raw_text,
                       order_index: editMaterial.order_index,
                       category: editCategory };
    try {
      const isReclassifiable = (RECLASSIFIABLE_TYPES as readonly string[])
        .includes(editMaterial.material_type);
      await updateMaterial(editMaterial.id, {
        title: editMaterial.title,
        raw_text: editMaterial.raw_text || '',
        // ai_summary 是多語系 JSONB，直接回傳整個物件
        ai_summary: editMaterial.ai_summary || undefined,
        order_index: editMaterial.order_index,
        // 僅可重分類類型才送 category；PPT/PDF 不動分類
        ...(isReclassifiable ? { category: editCategory.trim() } : {}),
      });
      // 同時更新 tags（覆蓋語意）
      await setMaterialTags(editMaterial.id, editTagIds);
      logUpdate(FUNC_CODE, original,
        { ...original, tag_ids: editTagIds });
      showSuccess('SYS020002', { name: editMaterial.title });
      setEditMaterial(null);
      await load();
    } catch (e: any) {
      logUpdate(FUNC_CODE, original,
        { ...original, tag_ids: editTagIds }, String(e?.message || e));
      showApiError(e);
    }
  };

  // ---------- Tag 字典 CRUD ----------
  const openCreateTag = () => {
    setEditingTag(null);
    setTagForm({
      tag_code: '', tag_name: '', description: '', color: '#1976d2',
      sort_order: tags.length * 10, is_active: true,
    });
    setTagDialogOpen(true);
  };

  const openEditTag = (tagObj: OpsTag) => {
    setEditingTag(tagObj);
    setTagForm({
      tag_code: tagObj.tag_code, tag_name: tagObj.tag_name,
      description: tagObj.description ?? '', color: tagObj.color,
      sort_order: tagObj.sort_order, is_active: tagObj.is_active,
    });
    setTagDialogOpen(true);
  };

  const handleSaveTag = async () => {
    if (!center) return;
    if (!tagForm.tag_code.trim() || !tagForm.tag_name.trim()) {
      showError('ERR020006', { field: '', detail: t('opsCenter.detail.messages.tagsRequired') });
      return;
    }
    try {
      if (editingTag) {
        await updateTag(editingTag.id, {
          tag_name: tagForm.tag_name,
          description: tagForm.description,
          color: tagForm.color,
          sort_order: tagForm.sort_order,
          is_active: tagForm.is_active,
        });
        logUpdate(FUNC_CODE,
          { tag_id: editingTag.id, tag_name: editingTag.tag_name, color: editingTag.color },
          { tag_id: editingTag.id, ...tagForm });
        showSuccess('SYS020002', { name: tagForm.tag_name });
      } else {
        const created = await createTag(center.id, tagForm);
        logCreate(FUNC_CODE, { action: 'create_tag', ...created });
        showSuccess('SYS020001', { name: tagForm.tag_name });
      }
      setTagDialogOpen(false);
      await loadTags();
    } catch (e: any) {
      const intent = editingTag
        ? { action: 'update_tag', tag_id: editingTag.id, ...tagForm }
        : { action: 'create_tag', center_id: center.id, ...tagForm };
      if (editingTag) {
        logUpdate(FUNC_CODE, { tag_id: editingTag.id, ...editingTag }, intent, String(e?.message || e));
      } else {
        logCreate(FUNC_CODE, intent, String(e?.message || e));
      }
      showApiError(e);
    }
  };

  const handleDeleteTag = async (tagObj: OpsTag) => {
    if (!window.confirm(t('opsCenter.detail.tagMgmt.deleteConfirm', { name: tagObj.tag_name }))) return;
    try {
      await deleteTag(tagObj.id);
      logDelete(FUNC_CODE, { action: 'delete_tag', tag_id: tagObj.id, tag_name: tagObj.tag_name });
      showSuccess('SYS020003', { name: tagObj.tag_name });
      await loadTags();
      await load();   // material 上的 tag_ids 跟著刷新
    } catch (e: any) {
      logDelete(FUNC_CODE, { tag_id: tagObj.id, tag_name: tagObj.tag_name }, String(e?.message || e));
      showApiError(e);
    }
  };

  // ---------- 批次掛 tag ----------
  const handleBulkAssignTags = async () => {
    if (!center || selectedIds.size === 0 || bulkTagIds.length === 0) return;
    const intent = {
      action: 'bulk_assign_tags',
      center_id: center.id,
      material_ids: Array.from(selectedIds),
      tag_ids: bulkTagIds,
    };
    try {
      const r = await bulkAssignTags(center.id, Array.from(selectedIds), bulkTagIds);
      if (r.ok) {
        logCreate(FUNC_CODE, { ...intent, result: r });
        showSuccess('SYS020002', { name: r.message });
      } else {
        logCreate(FUNC_CODE, intent, r.message);
        showError('ERR020004', { detail: r.message });
      }
      setBulkTagDialogOpen(false);
      setBulkTagIds([]);
      setSelectedIds(new Set());
      await load();
    } catch (e: any) {
      logCreate(FUNC_CODE, intent, String(e?.message || e));
      showApiError(e);
    }
  };

  // ---------- Tag 報告：點 tag 展開素材 ----------
  const handleExpandTag = async (tagId: number) => {
    if (!center) return;
    if (expandedTagId === tagId) {
      setExpandedTagId(null);
      setTagMaterials([]);
      return;
    }
    try {
      const r = await listMaterialsByTag(center.id, tagId);
      setTagMaterials(r);
      setExpandedTagId(tagId);
    } catch (e) { showApiError(e); }
  };

  // 批次重新分類：只對可重分類類型（image/research_item/note）套用，PPT/PDF 跳過
  const handleBulkReclassify = async () => {
    const cat = bulkCategory.trim();
    if (selectedIds.size === 0) return;
    // 篩出可重分類的素材（PPT/PDF 不動）
    const reclassifiableIds: number[] = [];
    let skippedPptPdf = 0;
    materials.forEach((m) => {
      if (!selectedIds.has(m.id)) return;
      if ((RECLASSIFIABLE_TYPES as readonly string[]).includes(m.material_type)) {
        reclassifiableIds.push(m.id);
      } else {
        skippedPptPdf += 1;
      }
    });
    if (reclassifiableIds.length === 0) {
      showError('ERR020004', { detail: t('opsCenter.detail.messages.reclassifyAllPptPdf') });
      return;
    }
    setBulkDialogOpen(false);

    let okCount = 0;
    const failed: number[] = [];
    for (const id of reclassifiableIds) {
      try {
        await updateMaterial(id, { category: cat });
        okCount += 1;
      } catch {
        failed.push(id);
      }
    }
    setSelectedIds(new Set());
    setBulkCategory('');
    await load();

    const skipMsg = skippedPptPdf > 0
      ? t('opsCenter.detail.messages.reclassifyOkSkipPptPdf', { count: skippedPptPdf })
      : '';
    const catMsg = cat
      ? t('opsCenter.detail.messages.reclassifyOkCatSet', { cat })
      : t('opsCenter.detail.messages.reclassifyOkCatRemoved');
    if (failed.length === 0) {
      showSuccess('SYS020002', {
        name: t('opsCenter.detail.messages.reclassifyOk',
          { ok: okCount, cat: catMsg, skip: skipMsg }),
      });
    } else {
      showError('ERR020004', {
        detail: t('opsCenter.detail.messages.reclassifyPartial',
          { ok: okCount, total: reclassifiableIds.length, ids: failed.join(', #') }),
      });
    }
  };

  // 以來源把 materials 分組
  interface MaterialGroup {
    key: string;
    label: string;
    type: 'pptx' | 'pdf' | 'research' | 'image' | 'note' | 'other' | 'category';
    materials: OpsMaterial[];
  }
  const groups = useMemo<MaterialGroup[]>(() => {
    const map = new Map<string, MaterialGroup>();
    // 保持穩定順序：先看到的 group 先出現
    materials.forEach((m) => {
      const meta = (m.source_metadata ?? {}) as Record<string, unknown>;
      const cat = typeof meta.category === 'string' ? meta.category.trim() : '';
      let key: string;
      let label: string;
      let type: MaterialGroup['type'];

      // 有 category 的素材優先按主題分類分組（跨檔案的同主題會聚在一起）
      if (cat) {
        key = `cat:${cat}`;
        label = cat;
        type = 'category';
      } else if (m.material_type === 'pptx_slide') {
        const raw = String(meta.filename ?? t('opsCenter.detail.library.groupLabels.pptxUnnamed'));
        const cleaned = raw.replace(/^[a-f0-9]{8}_/i, '');
        key = `pptx:${raw}`;
        label = cleaned;
        type = 'pptx';
      } else if (m.material_type === 'pdf_page') {
        const raw = String(meta.filename ?? t('opsCenter.detail.library.groupLabels.pdfUnnamed'));
        const cleaned = raw.replace(/^[a-f0-9]{8}_/i, '');
        key = `pdf:${raw}`;
        label = cleaned;
        type = 'pdf';
      } else if (m.material_type === 'research_item') {
        const topicCode = String(meta.topic_code ?? 'unknown');
        const topicName = String(meta.topic_name ?? topicCode);
        key = `research:${topicCode}`;
        label = t('opsCenter.detail.library.groupLabels.researchPrefix', { name: topicName });
        type = 'research';
      } else if (m.material_type === 'image') {
        key = 'images';
        label = t('opsCenter.detail.library.groupLabels.image');
        type = 'image';
      } else if (m.material_type === 'note') {
        key = 'notes';
        label = t('opsCenter.detail.library.groupLabels.note');
        type = 'note';
      } else {
        key = `other:${m.material_type}`;
        label = t('opsCenter.detail.library.groupLabels.other', { type: m.material_type });
        type = 'other';
      }

      if (!map.has(key)) {
        map.set(key, { key, label, type, materials: [] });
      }
      map.get(key)!.materials.push(m);
    });
    return Array.from(map.values());
  }, [materials, t]);

  // 首次載入後，預設把所有組收合（後續使用者操作不再覆寫）
  useEffect(() => {
    if (!collapseInitRef.current && groups.length > 0) {
      setCollapsedGroups(new Set(groups.map((g) => g.key)));
      collapseInitRef.current = true;
    }
  }, [groups]);

  const groupIcon = (type: MaterialGroup['type']) => {
    if (type === 'category') return <CategoryIcon fontSize="small" />;
    if (type === 'pptx') return <SlideshowIcon fontSize="small" />;
    if (type === 'pdf') return <PdfIcon fontSize="small" />;
    if (type === 'research') return <ResearchIcon fontSize="small" />;
    if (type === 'image') return <ImageIcon fontSize="small" />;
    return <FileIcon fontSize="small" />;
  };

  // tag id → tag 物件（快速查找）
  const tagById = useMemo(() => {
    const m = new Map<number, OpsTag>();
    tags.forEach((t) => m.set(t.id, t));
    return m;
  }, [tags]);

  // Table row renderer（同一段邏輯被每個 group 共用）
  const renderRow = (m: OpsMaterial) => {
    const snippet = getI18nValue(m.ai_summary, i18n.language);
    const hasSummary = !!m.ai_summary && Object.keys(m.ai_summary).length > 0;
    const reclassifiable = (RECLASSIFIABLE_TYPES as readonly string[])
      .includes(m.material_type);
    const checked = selectedIds.has(m.id);
    const matTags = (m.tag_ids ?? []).map((id) => tagById.get(id)).filter(Boolean) as OpsTag[];
    return (
      <TableRow key={m.id} hover selected={checked}>
        <TableCell padding="checkbox" align="center">
          {/* 全類型都可勾選；PPT/PDF 雖不可改分類，但可掛 tag */}
          <Tooltip title={reclassifiable
            ? t('opsCenter.detail.library.rowTooltips.checkboxReclassifiable')
            : t('opsCenter.detail.library.rowTooltips.checkboxPptPdf')}>
            <Checkbox size="small" checked={checked}
              onChange={(e) => {
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (e.target.checked) next.add(m.id); else next.delete(m.id);
                  return next;
                });
              }}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="center"><code>#{m.id}</code></TableCell>
        <TableCell align="center">
          {m.preview_image_path || m.file_path ? (
            <AuthImage src={getMaterialImageUrl(m.id)} alt=""
                       style={{ width: 60, height: 34, objectFit: 'cover', borderRadius: 3 }}
                       fallback={<ImageIcon fontSize="small" color="disabled" />} />
          ) : <ImageIcon fontSize="small" color="disabled" />}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.title}</Typography>
          {snippet && (
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', maxWidth: 500 }}>
              {snippet.slice(0, 120)}{snippet.length > 120 ? '…' : ''}
            </Typography>
          )}
          {matTags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {matTags.map((tg) => (
                <Chip key={tg.id} size="small" label={tg.tag_name}
                      sx={{ bgcolor: tg.color, color: '#fff', height: 18, fontSize: 11 }} />
              ))}
            </Box>
          )}
        </TableCell>
        <TableCell>
          <Chip size="small" label={m.material_type} />
        </TableCell>
        <TableCell align="center">{m.order_index}</TableCell>
        <TableCell align="center">
          {canUpdate && (
            <Tooltip title={hasSummary
              ? t('opsCenter.detail.library.rowTooltips.reanalyze')
              : t('opsCenter.detail.library.rowTooltips.analyze')}>
              <span>
                <IconButton size="small"
                            color={hasSummary ? 'default' : 'primary'}
                            disabled={analyzing === m.id}
                            onClick={() => handleAnalyzeOne(m)}>
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title={t('opsCenter.detail.library.rowTooltips.edit')}>
              <IconButton size="small" onClick={() => setEditMaterial(m)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title={t('opsCenter.detail.library.rowTooltips.delete')}>
              <IconButton size="small" color="error" onClick={() => handleDelete(m)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    );
  };

  if (loading && !center) return <Box sx={{ p: 3 }}><Typography>{t('opsCenter.list.loading')}</Typography></Box>;
  if (!center) return <Box sx={{ p: 3 }}><Typography>{t('opsCenter.detail.actions.notFound')}</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <FunctionPageHeader funcCode="ee_ops_centers" fallback={center.name} />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <IconButton size="small" onClick={() => navigate('/ee_ops_centers')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>{center.name}</Typography>
          <Chip size="small" label={`#${center.id}`} variant="outlined" />
          <Chip size="small" label={center.code} color="primary" />
          {center.research_topic_code && (
            <Chip size="small" label={t('opsCenter.detail.researchChip', { code: center.research_topic_code })} />
          )}
        </Box>
        {center.description && (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {center.description}
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" startIcon={<SlideshowIcon />}
                  onClick={() => navigate(`/ee_ops_centers/${center.code}/present`)}
                  size="large">
            {t('opsCenter.detail.actions.enterPresent')}
          </Button>
          {/* PPT / PDF：單檔，分類=檔名 */}
          <input ref={docFileRef} type="file" accept=".pptx,.pdf"
                 style={{ display: 'none' }}
                 onChange={(e) => {
                   const fs = Array.from(e.target.files || []);
                   if (fs.length > 0) {
                     setPendingFiles(fs.slice(0, 1));  // 單檔
                     setUploadCategory('');
                     setUploadDialogOpen(true);
                   }
                   if (docFileRef.current) docFileRef.current.value = '';
                 }} />
          {canCreate && (
            <Button variant="outlined" startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                    onClick={() => docFileRef.current?.click()}>
              {uploading && pendingFiles.length === 1
                ? t('opsCenter.detail.actions.uploading')
                : t('opsCenter.detail.actions.uploadPpt')}
            </Button>
          )}

          {/* 照片 / 圖片：多檔，整批共用同一個時間戳分類 */}
          <input ref={imgFileRef} type="file" multiple
                 accept=".jpg,.jpeg,.png,.webp,.gif"
                 style={{ display: 'none' }}
                 onChange={(e) => {
                   const fs = Array.from(e.target.files || []);
                   if (fs.length > 0) {
                     setPendingFiles(fs);
                     setUploadCategory('');
                     setUploadDialogOpen(true);
                   }
                   if (imgFileRef.current) imgFileRef.current.value = '';
                 }} />
          {canCreate && (
            <Button variant="outlined" startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                    onClick={() => imgFileRef.current?.click()}>
              {uploading && pendingFiles.length > 1
                ? (uploadProgress
                  ? t('opsCenter.detail.actions.uploadingProgress',
                      { current: uploadProgress.current, total: uploadProgress.total })
                  : t('opsCenter.detail.actions.uploading'))
                : t('opsCenter.detail.actions.uploadImage')}
            </Button>
          )}
          {canCreate && (
            <Button variant="outlined" startIcon={<CloudDownloadIcon />}
                    onClick={() => {
                      setImportTopicCode(center.research_topic_code || '');
                      setImportOpen(true);
                    }}>
              {t('opsCenter.detail.actions.importResearch')}
            </Button>
          )}
          {canUpdate && (
            <Button variant="outlined" startIcon={<AutoAwesomeIcon />}
                    disabled={analyzing === 'bulk'}
                    onClick={handleBulkAnalyze}>
              {analyzing === 'bulk'
                ? t('opsCenter.detail.actions.bulkAnalyzing')
                : t('opsCenter.detail.actions.bulkAnalyze')}
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>{t('opsCenter.detail.actions.refresh')}</Button>
        </Box>
      </Paper>

      {/* 主 Tabs */}
      <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v as TabKey)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="library" icon={<FileIcon fontSize="small" />} iconPosition="start"
             label={`${t('opsCenter.detail.tabs.library')}（${materials.length}）`} />
        <Tab value="tags" icon={<TagIcon fontSize="small" />} iconPosition="start"
             label={`${t('opsCenter.detail.tabs.tags')}（${tags.length}）`} />
        <Tab value="report" icon={<ReportIcon fontSize="small" />} iconPosition="start"
             label={t('opsCenter.detail.tabs.report')} />
      </Tabs>

      {/* === Tab: 素材庫 === */}
      {currentTab === 'library' && <>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {t('opsCenter.detail.library.summary', { count: materials.length, groups: groups.length })}
        </Typography>
        {selectedIds.size > 0 && (() => {
          // 計算選取明細：可重分類 vs PPT/PDF
          const selectedMats = materials.filter((m) => selectedIds.has(m.id));
          const reclassifiableCount = selectedMats.filter((m) =>
            (RECLASSIFIABLE_TYPES as readonly string[]).includes(m.material_type)
          ).length;
          const pptPdfCount = selectedMats.length - reclassifiableCount;
          return (
            <>
              <Chip color="primary" size="small"
                    label={pptPdfCount > 0
                      ? t('opsCenter.detail.library.selectedDetail', {
                          total: selectedIds.size,
                          reclassifiable: reclassifiableCount,
                          pptpdf: pptPdfCount,
                        })
                      : t('opsCenter.detail.library.selectedTotal', { count: selectedIds.size })} />
              {canUpdate && (
                <Tooltip title={reclassifiableCount === 0
                  ? t('opsCenter.detail.library.toolbarTooltips.reclassifyAllPptPdf')
                  : (pptPdfCount > 0 ? t('opsCenter.detail.library.toolbarTooltips.reclassifyMixed') : '')}>
                  <span>
                    <Button size="small" variant="contained" startIcon={<CategoryIcon />}
                            disabled={reclassifiableCount === 0}
                            onClick={() => { setBulkCategory(''); setBulkDialogOpen(true); }}>
                      {t('opsCenter.detail.library.reclassify')}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {canCreate && (
                <Tooltip title={tags.length === 0
                  ? t('opsCenter.detail.library.toolbarTooltips.bulkTagNoTags')
                  : t('opsCenter.detail.library.toolbarTooltips.bulkTagOk')}>
                  <span>
                    <Button size="small" variant="contained" color="secondary" startIcon={<TagIcon />}
                            onClick={() => { setBulkTagIds([]); setBulkTagDialogOpen(true); }}
                            disabled={tags.length === 0}>
                      {t('opsCenter.detail.library.bulkAssignTag')}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {canDelete && (
                <Button size="small" variant="contained" color="error" startIcon={<DeleteIcon />}
                        onClick={() => setBulkDeleteOpen(true)}>
                  {t('opsCenter.detail.library.bulkDelete', { count: selectedIds.size })}
                </Button>
              )}
              <Button size="small" onClick={() => setSelectedIds(new Set())}>
                {t('opsCenter.detail.library.clearSelection')}
              </Button>
            </>
          );
        })()}
        {groups.length > 0 && (
          <>
            <Button size="small"
                    onClick={() => setCollapsedGroups(new Set(groups.map((g) => g.key)))}>
              {t('opsCenter.detail.library.collapseAll')}
            </Button>
            <Button size="small"
                    onClick={() => setCollapsedGroups(new Set())}>
              {t('opsCenter.detail.library.expandAll')}
            </Button>
          </>
        )}
      </Box>

      {groups.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          {t('opsCenter.detail.library.empty')}
        </Paper>
      )}

      {groups.map((group) => {
        const isCollapsed = collapsedGroups.has(group.key);
        const page = groupPage[group.key] ?? 0;
        const total = group.materials.length;
        const start = page * rowsPerPage;
        const paged = group.materials.slice(start, start + rowsPerPage);
        const showPagination = total > rowsPerPage;

        return (
          <Paper key={group.key} variant="outlined" sx={{ mb: 1.5 }}>
            {/* 組 header — 可點擊收折 */}
            <Box onClick={() => toggleGroup(group.key)}
                 sx={{
                   display: 'flex', alignItems: 'center', gap: 1,
                   px: 1.5, py: 1, cursor: 'pointer',
                   bgcolor: 'grey.50',
                   borderBottom: isCollapsed ? 'none' : '1px solid',
                   borderBottomColor: 'divider',
                   '&:hover': { bgcolor: 'grey.100' },
                 }}>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
              </IconButton>
              {groupIcon(group.type)}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                {group.label}
              </Typography>
              <Chip size="small" label={t('opsCenter.detail.library.groupItems', { count: total })} />
              {/* 只播這組（僅以 category 為依據的組才顯示；研究議題舊資料無 category 不適用）*/}
              {(group.type === 'category' || group.type === 'pptx'
                 || group.type === 'pdf') && (
                <Tooltip title={t('opsCenter.detail.library.rowTooltips.playGroup', { label: group.label })}>
                  <IconButton size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/ee_ops_centers/${center?.code}/present?category=${encodeURIComponent(group.label)}`
                                );
                              }}>
                    <PlayCircleIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* 組內表格 + 分頁 */}
            <Collapse in={!isCollapsed} unmountOnExit>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" align="center">
                      {/* 全組勾選（涵蓋所有類型；PPT/PDF 也能勾，可批次掛 tag） */}
                      {(() => {
                        const selectableIds = group.materials.map((m) => m.id);
                        if (selectableIds.length === 0) return null;
                        const allSelected = selectableIds.every((id) => selectedIds.has(id));
                        const someSelected = selectableIds.some((id) => selectedIds.has(id));
                        return (
                          <Checkbox size="small"
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                            onChange={(e) => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) {
                                  selectableIds.forEach((id) => next.add(id));
                                } else {
                                  selectableIds.forEach((id) => next.delete(id));
                                }
                                return next;
                              });
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell width={60} align="center">#</TableCell>
                    <TableCell width={60} align="center">{t('opsCenter.detail.library.headers.preview')}</TableCell>
                    <TableCell>{t('opsCenter.detail.library.headers.title')}</TableCell>
                    <TableCell width={100}>{t('opsCenter.detail.library.headers.type')}</TableCell>
                    <TableCell width={90} align="center">{t('opsCenter.detail.library.headers.order')}</TableCell>
                    <TableCell width={170} align="center">{t('opsCenter.detail.library.headers.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map(renderRow)}
                </TableBody>
                {showPagination && (
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        colSpan={7}
                        count={total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        onPageChange={(_, newPage) =>
                          setGroupPage((prev) => ({ ...prev, [group.key]: newPage }))
                        }
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setGroupPage({});  // 每頁筆數變更：全部回到第 1 頁
                        }}
                        labelRowsPerPage={t('opsCenter.detail.library.pagination.rowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) =>
                          t('opsCenter.detail.library.pagination.displayedRows',
                            { from, to, count })}
                      />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </Collapse>
          </Paper>
        );
      })}

      </>}
      {/* === Tab: 素材庫 結束 === */}

      {/* === Tab: 標籤管理 === */}
      {currentTab === 'tags' && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
              {t('opsCenter.detail.tagMgmt.title', { count: tags.length })}
            </Typography>
            {canCreate && (
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                      onClick={openCreateTag}>
                {t('opsCenter.detail.tagMgmt.addNew')}
              </Button>
            )}
          </Stack>
          {tags.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              {t('opsCenter.detail.tagMgmt.empty')}
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50}>{t('opsCenter.detail.tagMgmt.headers.color')}</TableCell>
                  <TableCell width={140}>{t('opsCenter.detail.tagMgmt.headers.code')}</TableCell>
                  <TableCell>{t('opsCenter.detail.tagMgmt.headers.name')}</TableCell>
                  <TableCell>{t('opsCenter.detail.tagMgmt.headers.description')}</TableCell>
                  <TableCell width={80} align="center">{t('opsCenter.detail.tagMgmt.headers.attached')}</TableCell>
                  <TableCell width={80} align="center">{t('opsCenter.detail.tagMgmt.headers.order')}</TableCell>
                  <TableCell width={80} align="center">{t('opsCenter.detail.tagMgmt.headers.active')}</TableCell>
                  <TableCell width={120} align="center">{t('opsCenter.detail.tagMgmt.headers.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tags.map((tg) => (
                  <TableRow key={tg.id} hover>
                    <TableCell>
                      <Box sx={{
                        width: 24, height: 24, borderRadius: '50%',
                        bgcolor: tg.color, border: '1px solid', borderColor: 'divider',
                      }} />
                    </TableCell>
                    <TableCell><code>{tg.tag_code}</code></TableCell>
                    <TableCell>
                      <Chip size="small" label={tg.tag_name}
                            sx={{ bgcolor: tg.color, color: '#fff', fontWeight: 500 }} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                      {tg.description || '—'}
                    </TableCell>
                    <TableCell align="center">{tg.material_count ?? 0}</TableCell>
                    <TableCell align="center">{tg.sort_order}</TableCell>
                    <TableCell align="center">
                      {tg.is_active
                        ? <Chip size="small" label={t('opsCenter.detail.tagMgmt.active')} color="success" />
                        : <Chip size="small" label={t('opsCenter.detail.tagMgmt.inactive')} />}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('opsCenter.detail.tagMgmt.tooltips.playTag', { name: tg.tag_name })}>
                        <span>
                          <IconButton size="small"
                                      disabled={(tg.material_count ?? 0) === 0}
                                      onClick={() => navigate(
                                        `/ee_ops_centers/${center.code}/present?tag_ids=${tg.id}`
                                      )}>
                            <PlayCircleIcon fontSize="small" color="primary" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {canUpdate && (
                        <Tooltip title={t('opsCenter.detail.tagMgmt.tooltips.edit')}>
                          <IconButton size="small" onClick={() => openEditTag(tg)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title={t('opsCenter.detail.tagMgmt.tooltips.delete')}>
                          <IconButton size="small" color="error"
                                      onClick={() => handleDeleteTag(tg)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {/* === Tab: Tag 彙整報告 === */}
      {currentTab === 'report' && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
              {t('opsCenter.detail.report.title')}
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadAggregate}
                    disabled={aggregating}>
              {aggregating
                ? t('opsCenter.detail.report.aggregating')
                : t('opsCenter.detail.report.refresh')}
            </Button>
          </Stack>
          {tagAggregate.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              {aggregating
                ? t('opsCenter.detail.report.aggregating')
                : t('opsCenter.detail.report.empty')}
            </Box>
          ) : (
            <Stack spacing={1}>
              {tagAggregate.map((entry) => {
                const expanded = expandedTagId === entry.tag.id;
                return (
                  <Paper key={entry.tag.id} variant="outlined">
                    <Box onClick={() => handleExpandTag(entry.tag.id)}
                         sx={{
                           display: 'flex', alignItems: 'center', gap: 1,
                           px: 2, py: 1.5, cursor: 'pointer',
                           '&:hover': { bgcolor: 'grey.50' },
                         }}>
                      <IconButton size="small" sx={{ p: 0.25 }}>
                        {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                      </IconButton>
                      <Chip size="small" label={entry.tag.tag_name}
                            sx={{ bgcolor: entry.tag.color, color: '#fff', fontWeight: 500 }} />
                      <Typography variant="caption" color="text.secondary">
                        {entry.tag.tag_code}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Chip size="small" label={t('opsCenter.detail.report.stats.materials', { count: entry.material_count })} />
                      <Chip size="small" variant="outlined"
                            label={t('opsCenter.detail.report.stats.categories', { count: entry.category_count })} />
                      <Chip size="small" variant="outlined"
                            label={t('opsCenter.detail.report.stats.types', { count: entry.type_count })} />
                      {/* 只播這 tag */}
                      <Tooltip title={t('opsCenter.detail.report.playTag', { name: entry.tag.tag_name })}>
                        <span>
                          <IconButton size="small" color="primary"
                                      disabled={entry.material_count === 0}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/ee_ops_centers/${center?.code}/present?tag_ids=${entry.tag.id}`
                                        );
                                      }}>
                            <PlayCircleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    {entry.sample_categories.length > 0 && !expanded && (
                      <Box sx={{ px: 6, pb: 1.5, color: 'text.secondary', fontSize: 12 }}>
                        {t('opsCenter.detail.report.coveringCategories',
                          { names: entry.sample_categories.join('、') })}
                        {entry.category_count > entry.sample_categories.length ? '…' : ''}
                      </Box>
                    )}
                    <Collapse in={expanded} unmountOnExit>
                      <Divider />
                      {tagMaterials.length === 0 ? (
                        <Box sx={{ p: 2, color: 'text.secondary' }}>{t('opsCenter.detail.report.loading')}</Box>
                      ) : (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width={60}>{t('opsCenter.detail.report.headers.preview')}</TableCell>
                              <TableCell>{t('opsCenter.detail.report.headers.title')}</TableCell>
                              <TableCell width={120}>{t('opsCenter.detail.report.headers.type')}</TableCell>
                              <TableCell width={200}>{t('opsCenter.detail.report.headers.category')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tagMaterials.map((m) => {
                              const meta = (m.source_metadata ?? {}) as Record<string, unknown>;
                              const cat = typeof meta.category === 'string' ? meta.category : '—';
                              return (
                                <TableRow key={m.id}>
                                  <TableCell>
                                    {m.preview_image_path || m.file_path ? (
                                      <AuthImage src={getMaterialImageUrl(m.id)} alt=""
                                                 style={{ width: 60, height: 34,
                                                   objectFit: 'cover', borderRadius: 3 }}
                                                 fallback={<ImageIcon fontSize="small"
                                                                      color="disabled" />} />
                                    ) : <ImageIcon fontSize="small" color="disabled" />}
                                  </TableCell>
                                  <TableCell>{m.title}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={m.material_type} />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>{cat}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {/* 匯入對話框 */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('opsCenter.detail.dialogs.import.title')}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={topics}
            getOptionLabel={(opt) => `${opt.topic_code} · ${opt.topic_name}`}
            isOptionEqualToValue={(opt, val) => opt.topic_code === val.topic_code}
            value={topics.find((tp) => tp.topic_code === importTopicCode) ?? null}
            onChange={(_, val) => setImportTopicCode(val?.topic_code ?? '')}
            sx={{ mt: 1 }}
            renderInput={(params) => (
              <TextField {...params}
                label={t('opsCenter.detail.dialogs.import.topicCode')}
                helperText={t('opsCenter.detail.dialogs.import.helper')}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>{t('opsCenter.detail.dialogs.import.cancel')}</Button>
          <Button variant="contained" onClick={handleImport} disabled={!importTopicCode.trim()}>
            {t('opsCenter.detail.dialogs.import.import')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag CRUD 對話框 */}
      <Dialog open={tagDialogOpen} onClose={() => setTagDialogOpen(false)}
              maxWidth="sm" fullWidth>
        <DialogTitle>{editingTag
          ? t('opsCenter.detail.dialogs.tagCrud.titleEdit', { name: editingTag.tag_name })
          : t('opsCenter.detail.dialogs.tagCrud.titleAdd')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('opsCenter.detail.dialogs.tagCrud.tagCode')} value={tagForm.tag_code}
                       onChange={(e) => setTagForm({ ...tagForm, tag_code: e.target.value })}
                       disabled={!!editingTag}
                       helperText={editingTag
                         ? t('opsCenter.detail.dialogs.tagCrud.tagCodeHelperEdit')
                         : t('opsCenter.detail.dialogs.tagCrud.tagCodeHelperAdd')}
                       fullWidth required />
            <TextField label={t('opsCenter.detail.dialogs.tagCrud.tagName')} value={tagForm.tag_name}
                       onChange={(e) => setTagForm({ ...tagForm, tag_name: e.target.value })}
                       fullWidth required />
            <TextField label={t('opsCenter.detail.dialogs.tagCrud.description')} value={tagForm.description}
                       onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                       multiline rows={2} fullWidth />
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label={t('opsCenter.detail.dialogs.tagCrud.color')} type="color" value={tagForm.color}
                         onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                         sx={{ width: 120 }} />
              <Chip label={tagForm.tag_name || t('opsCenter.detail.dialogs.tagCrud.preview')}
                    sx={{ bgcolor: tagForm.color, color: '#fff', fontWeight: 500 }} />
              <TextField label={t('opsCenter.detail.dialogs.tagCrud.sortOrder')} type="number" value={tagForm.sort_order}
                         onChange={(e) => setTagForm({ ...tagForm, sort_order: parseInt(e.target.value) || 0 })}
                         sx={{ width: 100 }} />
              <FormControlLabel
                control={<Switch checked={tagForm.is_active}
                                 onChange={(e) => setTagForm({ ...tagForm, is_active: e.target.checked })} />}
                label={t('opsCenter.detail.dialogs.tagCrud.active')}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogOpen(false)}>{t('opsCenter.detail.dialogs.tagCrud.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveTag}>
            {editingTag
              ? t('opsCenter.detail.dialogs.tagCrud.save')
              : t('opsCenter.detail.dialogs.tagCrud.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批次刪除確認對話框 */}
      <Dialog open={bulkDeleteOpen} onClose={() => !bulkDeleting && setBulkDeleteOpen(false)}
              maxWidth="xs" fullWidth>
        <DialogTitle>{t('opsCenter.detail.dialogs.bulkDelete.title', { count: selectedIds.size })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('opsCenter.detail.dialogs.bulkDelete.confirm', { count: selectedIds.size })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
            {t('opsCenter.detail.dialogs.bulkDelete.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete} disabled={bulkDeleting}>
            {t('opsCenter.detail.dialogs.bulkDelete.submit', { count: selectedIds.size })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批次掛 Tag 對話框 */}
      <Dialog open={bulkTagDialogOpen} onClose={() => setBulkTagDialogOpen(false)}
              maxWidth="sm" fullWidth>
        <DialogTitle>{t('opsCenter.detail.dialogs.bulkTag.title', { count: selectedIds.size })}</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={tags}
            getOptionLabel={(opt) => opt.tag_name}
            value={tags.filter((tg) => bulkTagIds.includes(tg.id))}
            onChange={(_, vals) => setBulkTagIds(vals.map((v) => v.id))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id}
                      label={option.tag_name}
                      sx={{ bgcolor: option.color, color: '#fff' }}
                      size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params}
                label={t('opsCenter.detail.dialogs.bulkTag.label')}
                placeholder={t('opsCenter.detail.dialogs.bulkTag.placeholder')}
                helperText={t('opsCenter.detail.dialogs.bulkTag.helper')}
                autoFocus
                sx={{ mt: 1 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkTagDialogOpen(false)}>{t('opsCenter.detail.dialogs.bulkTag.cancel')}</Button>
          <Button variant="contained" onClick={handleBulkAssignTags}
                  disabled={bulkTagIds.length === 0}>
            {t('opsCenter.detail.dialogs.bulkTag.submit', { count: selectedIds.size })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 批次重新分類對話框（只對 image / research_item / note 開放） */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('opsCenter.detail.dialogs.bulkReclassify.title', { count: selectedIds.size })}</DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            value={bulkCategory}
            onInputChange={(_, val) => setBulkCategory(val ?? '')}
            options={groups
              .filter((g) => g.type === 'category')
              .map((g) => g.label)}
            renderInput={(params) => (
              <TextField {...params}
                label={t('opsCenter.detail.dialogs.bulkReclassify.label')}
                placeholder={t('opsCenter.detail.dialogs.bulkReclassify.placeholder')}
                helperText={t('opsCenter.detail.dialogs.bulkReclassify.helper')}
                autoFocus
                sx={{ mt: 1 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>{t('opsCenter.detail.dialogs.bulkReclassify.cancel')}</Button>
          <Button variant="contained" onClick={handleBulkReclassify}>
            {t('opsCenter.detail.dialogs.bulkReclassify.submit', { count: selectedIds.size })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 上傳素材對話框 — 依按鈕來源不同，PPT/PDF 用檔名、圖片用批次時間戳 */}
      <Dialog open={uploadDialogOpen}
              onClose={() => { if (!uploading) { setUploadDialogOpen(false); setPendingFiles([]); } }}
              maxWidth="sm" fullWidth>
        <DialogTitle>{(() => {
          const isDoc = pendingFiles.length === 1
            && /\.(pptx|pdf)$/i.test(pendingFiles[0].name);
          return isDoc
            ? t('opsCenter.detail.dialogs.upload.titlePptPdf')
            : t('opsCenter.detail.dialogs.upload.titleImages', { count: pendingFiles.length });
        })()}</DialogTitle>
        <DialogContent>
          <Box sx={{
            mt: 1, mb: 2, maxHeight: 200, overflow: 'auto',
            border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1,
          }}>
            {pendingFiles.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                  {f.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(f.size / 1024)} KB
                </Typography>
              </Box>
            ))}
          </Box>
          {(() => {
            const isDoc = pendingFiles.length === 1
              && /\.(pptx|pdf)$/i.test(pendingFiles[0].name);
            const helper = isDoc
              ? t('opsCenter.detail.dialogs.upload.categoryHelperDoc')
              : t('opsCenter.detail.dialogs.upload.categoryHelperImg');
            return (
              <Autocomplete
                freeSolo
                value={uploadCategory}
                onInputChange={(_, val) => setUploadCategory(val ?? '')}
                options={groups
                  .filter((g) => g.type === 'category')
                  .map((g) => g.label)}
                disabled={uploading}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('opsCenter.detail.dialogs.upload.categoryLabel')}
                    placeholder={isDoc
                      ? t('opsCenter.detail.dialogs.upload.categoryPlaceholderDoc')
                      : t('opsCenter.detail.dialogs.upload.categoryPlaceholderImg')}
                    helperText={helper}
                    autoFocus
                  />
                )}
              />
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setPendingFiles([]); }}
                  disabled={uploading}>
            {t('opsCenter.detail.dialogs.upload.cancel')}
          </Button>
          <Button variant="contained" onClick={handleConfirmUpload}
                  disabled={uploading || pendingFiles.length === 0}>
            {uploading
              ? t('opsCenter.detail.dialogs.upload.uploading')
              : t('opsCenter.detail.dialogs.upload.submit', { count: pendingFiles.length })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編輯素材對話框 */}
      <Dialog open={!!editMaterial} onClose={() => setEditMaterial(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('opsCenter.detail.dialogs.editMaterial.title', { id: editMaterial?.id })}</DialogTitle>
        <DialogContent>
          {editMaterial && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label={t('opsCenter.detail.dialogs.editMaterial.titleField')} value={editMaterial.title}
                         onChange={(e) => setEditMaterial({ ...editMaterial, title: e.target.value })}
                         fullWidth />
              {/* PPT/PDF 切片不開放再分類；其餘類型才顯示主題分類欄 */}
              {!(editMaterial.material_type === 'pptx_slide'
                 || editMaterial.material_type === 'pdf_page') && (
                <Autocomplete
                  freeSolo
                  value={editCategory}
                  onInputChange={(_, val) => setEditCategory(val ?? '')}
                  options={groups
                    .filter((g) => g.type === 'category')
                    .map((g) => g.label)}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('opsCenter.detail.dialogs.editMaterial.category')}
                      placeholder={t('opsCenter.detail.dialogs.editMaterial.categoryPlaceholder')}
                      helperText={t('opsCenter.detail.dialogs.editMaterial.categoryHelper')}
                    />
                  )}
                />
              )}
              <TextField
                label={t('opsCenter.detail.dialogs.editMaterial.aiSummary', { lang: i18n.language })}
                value={getI18nValue(editMaterial.ai_summary, i18n.language)}
                onChange={(e) => {
                  const updated = { ...(editMaterial.ai_summary || {}) };
                  updated[i18n.language] = e.target.value;
                  setEditMaterial({ ...editMaterial, ai_summary: updated });
                }}
                helperText={t('opsCenter.detail.dialogs.editMaterial.aiSummaryHelper')}
                         multiline rows={3} fullWidth />
              <TextField label={t('opsCenter.detail.dialogs.editMaterial.rawText')} value={editMaterial.raw_text || ''}
                         onChange={(e) => setEditMaterial({ ...editMaterial, raw_text: e.target.value })}
                         multiline rows={8} fullWidth />
              <TextField label={t('opsCenter.detail.dialogs.editMaterial.orderIndex')} type="number" value={editMaterial.order_index}
                         onChange={(e) => setEditMaterial({ ...editMaterial, order_index: parseInt(e.target.value) || 0 })} />
              {/* Tag 多選 — 從 tag 字典挑 */}
              <Autocomplete
                multiple
                options={tags}
                getOptionLabel={(opt) => opt.tag_name}
                value={tags.filter((tg) => editTagIds.includes(tg.id))}
                onChange={(_, vals) => setEditTagIds(vals.map((v) => v.id))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id}
                          label={option.tag_name}
                          sx={{ bgcolor: option.color, color: '#fff' }}
                          size="small" />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('opsCenter.detail.dialogs.editMaterial.tags')}
                    placeholder={tags.length === 0
                      ? t('opsCenter.detail.dialogs.editMaterial.tagsPlaceholderEmpty')
                      : t('opsCenter.detail.dialogs.editMaterial.tagsPlaceholder')}
                    helperText={t('opsCenter.detail.dialogs.editMaterial.tagsHelper')}
                  />
                )}
                disabled={tags.length === 0}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMaterial(null)}>{t('opsCenter.detail.dialogs.editMaterial.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveEdit}>{t('opsCenter.detail.dialogs.editMaterial.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpsCenterDetailPage;
